import cn from 'classnames';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { checkStatusOfKyc } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { RootState } from 'src/types/store-types';
import './styles.scss';

type Props = {
  selectedSetting: string;
  setSelectedSetting: (setting: string) => void;
  sections: any[];
};
const MobileSections: FC<Props> = ({ selectedSetting, setSelectedSetting, sections }) => {
  const { t } = useTranslation();

  const { bottomNavigation } = useSelector((state: RootState) => state.configs);
  const { kycStatus } = useSelector((state: RootState) => state.user);

  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const sessionRecorder = useSessionRecorder();

  const [selectedSubsection, setSelectedSubsection] = useState<string>('');

  const safeSections = (
    Array.isArray(sections) ? sections.filter((section) => section && typeof section === 'object') : []
  ).map((section) => {
    if (section?.name === 'info_and_rules') {
      const children = Array.isArray(section?.children) ? section.children : [];
      if (children.length === 0 && Array.isArray(bottomNavigation) && bottomNavigation.length > 0) {
        return {
          ...section,
          children: bottomNavigation.map((item) => ({
            name: item.name,
            title: item.title,
          })),
        };
      }
    }
    return section;
  });

  const changeSelectedSubsection = (subSection: string, section: string, subTitle?: string): void => {
    setSelectedSubsection(subSection);
    _openAccountModal({ section: section, subSection: subSection, subTitle: subTitle });
  };

  const toggleSetting = (section: Section) => () => {
    const children = Array.isArray(section?.children) ? section.children : [];
    if (children.length === 0 && section?.link?.section) {
      _openAccountModal({ section: section.link.section, subSection: section.link.subSection });
    }

    setSelectedSetting(selectedSetting === section.name ? '' : section.name);

    sessionRecorder?.trackEvent('Header user setting button click', {
      section,
    });
  };

  const setSubSectionList = useCallback(
    (subSections: any[], sectionName: string) => {
      return (
        <ul className="panel">
          {subSections.map((subSection, j: number) => (
            <li
              className="panel-list-item"
              key={subSection?.title ? j + subSection?.title : j + subSection}
              onClick={() =>
                changeSelectedSubsection(
                  subSection?.name ? subSection?.name : subSection,
                  sectionName,
                  subSection?.title
                )
              }
            >
              {t(subSection?.title ? subSection?.title : subSection)}
            </li>
          ))}
        </ul>
      );
    },
    [changeSelectedSubsection, selectedSubsection, t]
  );

  return (
    <>
      {safeSections.map((section: Section) => {
        const children = Array.isArray(section?.children) ? section.children : [];
        return (
          <li key={section.name}>
            <Button className="accordion" onClick={toggleSetting(section)} justifyContentValue="space-between">
              <div className="category">
                <SvgIcon src={`${section.icon}`} />
                {t(section.name)}
              </div>
              {children.length > 0 && (
                <SvgIcon
                  src={icons.arrowDown}
                  className={cn('arrow', { ['rotateArrow']: selectedSetting === section.name })}
                />
              )}
              {section?.notPassedIcon &&
                section?.passedIcon &&
                (checkStatusOfKyc(kycStatus) ? (
                  <img src={section?.passedIcon} alt="passedIcon" width="24" />
                ) : (
                  <img src={section?.notPassedIcon} alt="notPassedIcon" width="24" />
                ))}
            </Button>
            {!!children.length && selectedSetting === section.name && setSubSectionList(children, section.name)}
          </li>
        );
      })}
    </>
  );
};
export default MobileSections;
